import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Users, Package, Settings, AlertCircle, CheckCircle } from 'lucide-react';
import WhiteLabelSettings from '@/components/WhiteLabelSettings';
import UsageLimitWarning from '@/components/UsageLimitWarning';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TenantDetail = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [planConfig, setPlanConfig] = useState(null);
  const [usage, setUsage] = useState(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');

  useEffect(() => {
    loadTenant();
  }, [tenantId]);

  const loadTenant = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/super-admin/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenant(response.data.tenant);
      setPlanConfig(response.data.plan_config);
      setUsage(response.data.current_usage);
    } catch (error) {
      console.error('Load tenant error:', error);
      toast.error('Failed to load tenant');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async () => {
    if (!selectedPlan) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/super-admin/tenants/${tenantId}`,
        { plan: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Plan changed to ${selectedPlan}!`);
      setShowPlanDialog(false);
      loadTenant();
    } catch (error) {
      console.error('Plan change error:', error);
      toast.error('Failed to change plan');
    }
  };

  const handleSuspend = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/super-admin/tenants/${tenantId}`,
        { status: 'suspended', suspended_reason: suspendReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tenant suspended');
      setShowSuspendDialog(false);
      loadTenant();
    } catch (error) {
      console.error('Suspend error:', error);
      toast.error('Failed to suspend tenant');
    }
  };

  const handleActivate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/super-admin/tenants/${tenantId}`,
        { status: 'active' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tenant activated');
      loadTenant();
    } catch (error) {
      console.error('Activate error:', error);
      toast.error('Failed to activate tenant');
    }
  };

  const handleFeatureToggle = async (featureName, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/super-admin/tenants/${tenantId}/feature-flags`,
        { feature_flags: { [featureName]: enabled } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Feature ${featureName} ${enabled ? 'enabled' : 'disabled'}`);
      loadTenant();
    } catch (error) {
      console.error('Feature toggle error:', error);
      toast.error('Failed to toggle feature');
    }

  };

  const handleImpersonate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/impersonate/start/${tenantId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Save impersonation token
        localStorage.setItem('impersonation_token', response.data.impersonation_token);
        localStorage.setItem('impersonation_mode', 'true');
        
        toast.success('Impersonation started (read-only)', {
          description: `Logged in as ${response.data.user.name}`
        });
        
        // Navigate to tenant admin dashboard
        navigate('/admin');
      }
    } catch (error) {
      console.error('Impersonation error:', error);
      toast.error('Failed to start impersonation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!tenant) return <div>Tenant not found</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/super-admin/tenants')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{tenant.company_name}</h1>
            <p className="text-gray-500 mt-1">{tenant.subdomain}.flotto.app</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={tenant.plan === 'enterprise' ? 'default' : tenant.plan === 'pro' ? 'secondary' : 'outline'}>
            {tenant.plan?.toUpperCase()}
          </Badge>
          {tenant.status === 'suspended' ? (
            <>
              <Badge variant="destructive">SUSPENDED</Badge>
              <Button onClick={handleActivate}>Activate</Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={handleImpersonate}
                className="mr-2"
              >
                🔍 Login as Tenant
              </Button>
              <Button variant="destructive" onClick={() => setShowSuspendDialog(true)}>
                Suspend
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Usage Limit Warning */}
      <UsageLimitWarning tenant={tenant} usage={usage} planConfig={planConfig} />

      {/* Plan & Usage */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold">{planConfig?.name}</div>
              <div className="text-sm text-gray-500">€{planConfig?.price}/month</div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedPlan(tenant.plan);
                  setShowPlanDialog(true);
                }}
              >
                Change Plan
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Drivers:</span>
                <span className="font-medium">
                  {usage?.drivers || 0} / {planConfig?.limits?.max_drivers === -1 ? '∞' : planConfig?.limits?.max_drivers}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Locations:</span>
                <span className="font-medium">
                  {usage?.locations || 0} / {planConfig?.limits?.max_locations === -1 ? '∞' : planConfig?.limits?.max_locations}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Vehicles:</span>
                <span className="font-medium">{usage?.vehicles || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Active Shifts:</span>
                <span className="font-medium">{usage?.active_shifts || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Deliveries Today:</span>
                <span className="font-medium">{usage?.deliveries_today || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Details & White-Label */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details">Details & Features</TabsTrigger>
          {tenant.plan === 'enterprise' && (
            <TabsTrigger value="branding">White-Label Settings</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          {/* Feature Flags */}
          <Card>
            <CardHeader>
              <CardTitle>Feature Flags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(tenant.feature_flags || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      {value ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="text-sm font-medium">{key}</span>
                    </div>
                    <Switch
                      checked={value}
                      onCheckedChange={(checked) => handleFeatureToggle(key, checked)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {tenant.plan === 'enterprise' && (
          <TabsContent value="branding">
            <WhiteLabelSettings tenant={tenant} onUpdate={loadTenant} />
          </TabsContent>
        )}
      </Tabs>

      {/* Plan Change Dialog */}
      <AlertDialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Plan</AlertDialogTitle>
            <AlertDialogDescription>
              Select a new plan for {tenant.company_name}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            {['basic', 'pro', 'enterprise'].map((plan) => (
              <div
                key={plan}
                className={`p-4 border rounded-lg cursor-pointer ${
                  selectedPlan === plan ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                }`}
                onClick={() => setSelectedPlan(plan)}
              >
                <div className="font-semibold">{plan.toUpperCase()}</div>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePlanChange}>Change Plan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suspend Dialog */}
      <AlertDialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend Tenant</AlertDialogTitle>
            <AlertDialogDescription>
              This will suspend {tenant.company_name}. They will lose access to the platform.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label htmlFor="reason">Reason (optional)</Label>
            <input
              id="reason"
              className="w-full border rounded-md px-3 py-2 mt-1"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Payment failed, policy violation, etc."
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSuspend} className="bg-red-600">
              Suspend Tenant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TenantDetail;
